/**
 * Read More/Less Functionality
 * Handles truncating text by lines or words with configurable options
 */
(function() {
  'use strict';

  class ReadMore {
    constructor(container) {
      this.container = container;
      this.content = container.querySelector('[data-read-more-content]');
      this.button = container.querySelector('[data-read-more-button]');
      this.readMoreLabel = container.querySelector('[data-read-more-label]');
      this.readLessLabel = container.querySelector('[data-read-less-label]');
      this.truncateType = container.dataset.truncateType || 'lines';
      this.truncateLines = parseInt(container.dataset.truncateLines) || 5;
      this.truncateWords = parseInt(container.dataset.truncateWords) || 30;
      this.ellipsisText = container.dataset.ellipsisText || '...';
      this.buttonPlacement = container.dataset.buttonPlacement || 'inline';
      this.isExpanded = false;
      this.originalContent = null;
      this.truncatedContent = null;

      if (!this.content || !this.button) return;

      this.init();
    }

    init() {
      // Store original content
      this.originalContent = this.content.innerHTML;
      
      // Check if content needs truncation and create truncated version
      if (this.truncateType === 'lines') {
        this.initLineTruncation();
      } else {
        this.initWordTruncation();
      }
      
      // Add event listener
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    }

    initLineTruncation() {
      // Use CSS line-clamp for line truncation - much more reliable
      this.content.style.setProperty('--truncate-lines', this.truncateLines);
      this.content.classList.add('text__read-more-content--lines');
      this.content.classList.add('text__read-more-content--truncated');
      
      // Set initial state
      this.isExpanded = false;
      this.button.setAttribute('aria-expanded', 'false');
      
      if (this.readMoreLabel) this.readMoreLabel.style.display = 'inline';
      if (this.readLessLabel) this.readLessLabel.style.display = 'none';
    }

    initWordTruncation() {
      // Count words in the content
      const wordCount = this.getWordCount(this.originalContent);
      
      if (wordCount <= this.truncateWords) {
        // Hide button if content doesn't need truncation
        this.button.parentElement.style.display = 'none';
        return;
      }

      // Create truncated version by words
      this.createTruncatedByWords();
      
      // Set initial state (truncated)
      this.content.innerHTML = this.truncatedContent;
      this.content.classList.add('text__read-more-content--truncated');
      this.isExpanded = false;
      this.button.setAttribute('aria-expanded', 'false');
      
      if (this.readMoreLabel) this.readMoreLabel.style.display = 'inline';
      if (this.readLessLabel) this.readLessLabel.style.display = 'none';
    }

    getWordCount(html) {
      const temp = document.createElement('div');
      temp.innerHTML = html;
      const text = temp.textContent || temp.innerText || '';
      const words = text.trim().split(/\s+/).filter(word => word.length > 0);
      return words.length;
    }

    createTruncatedByWords() {
      // Get plain text and count words
      const temp = document.createElement('div');
      temp.innerHTML = this.originalContent;
      const textContent = temp.textContent || temp.innerText || '';
      const allWords = textContent.trim().split(/\s+/).filter(word => word.length > 0);
      
      if (allWords.length <= this.truncateWords) {
        this.truncatedContent = this.originalContent;
        return;
      }
      
      // Find the truncation point in the HTML by walking through text nodes
      let wordCount = 0;
      let truncated = false;
      
      const walkAndTruncate = (node) => {
        if (truncated || !node) return;
        
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || '';
          const words = text.trim().split(/\s+/).filter(w => w.length > 0);
          
          if (wordCount + words.length <= this.truncateWords) {
            wordCount += words.length;
          } else {
            // Need to truncate here
            const remainingWords = this.truncateWords - wordCount;
            const textWords = text.trim().split(/\s+/);
            const wordsToKeep = textWords.slice(0, remainingWords);
            
            // Find the position in original text
            let charPos = 0;
            let wordsFound = 0;
            for (let i = 0; i < text.length && wordsFound < remainingWords; i++) {
              if (/\s/.test(text[i]) && i > 0 && !/\s/.test(text[i - 1])) {
                wordsFound++;
              }
              charPos = i + 1;
            }
            
            // Truncate the text node
            node.textContent = text.substring(0, charPos).trim() + this.ellipsisText;
            
            // Remove all following nodes
            truncated = true;
            
            // Remove siblings
            let nextSibling = node.nextSibling;
            while (nextSibling) {
              const toRemove = nextSibling;
              nextSibling = nextSibling.nextSibling;
              toRemove.remove();
            }
          }
        } else if (node.nodeType === Node.ELEMENT_NODE && node.childNodes) {
          // Process children
          const children = Array.from(node.childNodes);
          for (const child of children) {
            if (!truncated) {
              walkAndTruncate(child);
            } else {
              child.remove();
            }
          }
        }
      };
      
      // Create a working copy
      const workingCopy = temp.cloneNode(true);
      walkAndTruncate(workingCopy);
      
      // Fallback if truncation didn't work
      if (!truncated || !workingCopy.innerHTML.trim()) {
        const truncatedWords = allWords.slice(0, this.truncateWords);
        this.truncatedContent = '<p>' + truncatedWords.join(' ') + this.ellipsisText + '</p>';
      } else {
        this.truncatedContent = workingCopy.innerHTML;
      }
    }

    toggle() {
      if (this.isExpanded) {
        this.collapse();
      } else {
        this.expand();
      }
    }

    expand() {
      if (this.truncateType === 'lines') {
        this.content.classList.remove('text__read-more-content--truncated');
      } else {
        this.content.innerHTML = this.originalContent;
      }
      
      this.isExpanded = true;
      this.button.setAttribute('aria-expanded', 'true');
      
      if (this.readMoreLabel) this.readMoreLabel.style.display = 'none';
      if (this.readLessLabel) this.readLessLabel.style.display = 'inline';
      
      this.content.classList.add('text__read-more-content--expanded');
      
      // Scroll button into view if it's below the content
      if (this.buttonPlacement === 'below') {
        setTimeout(() => {
          this.button.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }
    }

    collapse() {
      if (this.truncateType === 'lines') {
        this.content.classList.add('text__read-more-content--truncated');
      } else {
        this.content.innerHTML = this.truncatedContent;
      }
      
      this.isExpanded = false;
      this.button.setAttribute('aria-expanded', 'false');
      
      if (this.readMoreLabel) this.readMoreLabel.style.display = 'inline';
      if (this.readLessLabel) this.readLessLabel.style.display = 'none';
      
      this.content.classList.remove('text__read-more-content--expanded');
    }
  }

  // Initialize all read more instances
  function initReadMore() {
    const containers = document.querySelectorAll('[data-read-more-container]');
    containers.forEach(container => {
      // Don't re-initialize if already initialized
      if (!container.hasAttribute('data-read-more-initialized')) {
        container.setAttribute('data-read-more-initialized', 'true');
        new ReadMore(container);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReadMore);
  } else {
    initReadMore();
  }

  // Re-initialize on dynamic content load (for AJAX/Shopify theme editor)
  document.addEventListener('shopify:section:load', () => {
    // Reset initialized flag and re-init
    document.querySelectorAll('[data-read-more-initialized]').forEach(el => {
      el.removeAttribute('data-read-more-initialized');
    });
    initReadMore();
  });
  
  document.addEventListener('shopify:block:select', initReadMore);

})();
